import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-public-leave-review',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="min-h-screen bg-[#F3F8FB] px-4 py-8 sm:py-12">
      <main class="mx-auto max-w-2xl">
        <div class="mb-5 flex items-center gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-[#102C41] text-white"><i class="ti ti-calendar-check text-xl"></i></div>
          <div><p class="text-[10px] font-bold uppercase tracking-[.18em] text-[#1A6FA6]">NZ Plumbing</p><h1 class="text-xl font-semibold text-slate-950">Leave request review</h1></div>
        </div>

        @if (isLoading()) {
          <section class="rounded-2xl border border-slate-200 bg-white p-14 text-center shadow-sm"><i class="ti ti-loader animate-spin text-2xl text-[#1A6FA6]"></i><p class="mt-3 text-sm text-slate-500">Opening secure review...</p></section>
        } @else if (errorMessage()) {
          <section class="rounded-2xl border border-rose-100 bg-white p-10 text-center shadow-sm"><i class="ti ti-link-off text-4xl text-rose-300"></i><h2 class="mt-3 font-semibold text-slate-900">Link unavailable</h2><p class="mt-1 text-sm text-slate-500">{{ errorMessage() }}</p></section>
        } @else if (request()) {
          <section class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div class="border-b border-slate-100 p-5 sm:p-6 flex items-start justify-between gap-4">
              <div><p class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Submitted by</p><h2 class="mt-1 text-lg font-semibold text-slate-950">{{ request().employee_name }}</h2><p class="text-sm text-slate-500">{{ request().leave_type }}</p></div>
              <span class="rounded-lg border px-2.5 py-1 text-[10px] font-bold uppercase" [ngClass]="statusClass(request().status)">{{ request().status }}</span>
            </div>
            <div class="grid grid-cols-2 gap-px bg-slate-100">
              <div class="bg-white p-4 sm:px-6"><p class="label">Leave dates</p><p class="value">{{ request().start_date | date:'dd MMM yyyy' }} – {{ request().end_date | date:'dd MMM yyyy' }}</p></div>
              <div class="bg-white p-4 sm:px-6"><p class="label">Duration</p><p class="value">{{ request().total_days }} day(s) · {{ request().is_half_day == 1 ? 'Half day included' : 'Full day' }}</p></div>
            </div>
            <div class="space-y-5 p-5 sm:p-6">
              <div><p class="label">Reason for leave</p><p class="detail">{{ request().reason }}</p></div>
              @if (request().handover_notes) { <div><p class="label">Handover notes</p><p class="detail">{{ request().handover_notes }}</p></div> }
              @if (request().contact_during_leave) { <div><p class="label">Contact while away</p><p class="detail">{{ request().contact_during_leave }}</p></div> }
              @if (request().expected_due_or_adoption_date) { <div><p class="label">Expected due/adoption date</p><p class="detail">{{ request().expected_due_or_adoption_date | date:'dd MMM yyyy' }}</p></div> }
              @if (request().proof_note || request().proof_file_url) {
                <div class="rounded-xl border border-amber-100 bg-amber-50 p-4"><p class="label !text-amber-700">Supporting proof</p>@if (request().proof_note) { <p class="detail !text-amber-900">{{ request().proof_note }}</p> }@if (request().proof_file_url) { <a [href]="assetUrl(request().proof_file_url)" target="_blank" class="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-900 underline"><i class="ti ti-paperclip"></i>Open proof file</a> }</div>
              }
            </div>

            @if (completedStatus()) {
              <div class="border-t border-emerald-100 bg-emerald-50 p-6 text-center"><i class="ti ti-circle-check text-3xl text-emerald-600"></i><h3 class="mt-2 font-semibold text-emerald-900">Request {{ completedStatus()?.toLowerCase() }}</h3><p class="mt-1 text-xs text-emerald-700">The employee has been notified by email.</p></div>
            } @else if (request().can_decide) {
              <div class="border-t border-slate-100 bg-slate-50 p-5 sm:p-6">
                <p class="text-sm font-bold text-slate-900">Your decision</p>
                <p class="mt-1 text-xs text-slate-500">You are reviewing this request as {{ request().reviewer_name }}. No login is required.</p>
                @if (decision()) {
                  <label class="mt-4 block text-xs font-semibold text-slate-600">Admin note {{ decision() === 'Declined' ? '*' : '(optional)' }}<textarea [(ngModel)]="adminNotes" rows="3" class="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Add a note for the employee"></textarea></label>
                }
                <div class="mt-4 grid grid-cols-2 gap-3">
                  <button (click)="chooseDecision('Approved')" [disabled]="isSaving()" class="h-11 rounded-xl bg-emerald-600 text-sm font-bold text-white disabled:opacity-50">{{ decision() === 'Approved' ? 'Confirm approval' : 'Approve' }}</button>
                  <button (click)="chooseDecision('Declined')" [disabled]="isSaving()" class="h-11 rounded-xl bg-rose-600 text-sm font-bold text-white disabled:opacity-50">{{ decision() === 'Declined' ? 'Confirm decline' : 'Decline' }}</button>
                </div>
                @if (actionError()) { <p class="mt-3 text-center text-xs font-semibold text-rose-600">{{ actionError() }}</p> }
              </div>
            } @else {
              <div class="border-t border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">This request has already been reviewed or your approval permission is no longer active.</div>
            }
          </section>
          <p class="mt-4 text-center text-[11px] text-slate-400">This secure link is unique to its recipient and expires after seven days.</p>
        }
      </main>
    </div>
  `,
  styles: [`.label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#94a3b8}.value{margin-top:3px;font-size:13px;font-weight:600;color:#0f172a}.detail{margin-top:4px;font-size:13px;line-height:1.6;color:#475569;white-space:pre-line}`]
})
export class PublicLeaveReviewComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private token = '';
  request = signal<any>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  errorMessage = signal('');
  actionError = signal('');
  decision = signal<'Approved' | 'Declined' | null>(null);
  completedStatus = signal<'Approved' | 'Declined' | null>(null);
  adminNotes = '';

  ngOnInit() {
    this.token = this.route.snapshot.paramMap.get('token') || '';
    this.api.getPublicLeaveReview(this.token).subscribe({
      next: (res: any) => { this.request.set(res.data?.leave_request || null); this.isLoading.set(false); },
      error: (err) => { this.errorMessage.set(err.error?.message || 'This link is invalid or has expired.'); this.isLoading.set(false); }
    });
  }

  chooseDecision(type: 'Approved' | 'Declined') {
    if (this.decision() !== type) { this.decision.set(type); this.actionError.set(''); return; }
    if (type === 'Declined' && !this.adminNotes.trim()) { this.actionError.set('Please enter a reason for declining.'); return; }
    this.isSaving.set(true);
    this.actionError.set('');
    this.api.submitPublicLeaveReview({ token: this.token, decision: type, admin_notes: this.adminNotes }).subscribe({
      next: () => { this.isSaving.set(false); this.completedStatus.set(type); this.request.update(value => ({ ...value, status: type, can_decide: false })); },
      error: (err) => { this.isSaving.set(false); this.actionError.set(err.error?.message || 'Could not save this decision.'); }
    });
  }

  assetUrl(path: string) { return this.api.assetUrl(path); }
  statusClass(status: string) {
    if (status === 'Approved') return 'border-emerald-100 bg-emerald-50 text-emerald-700';
    if (status === 'Declined' || status === 'Cancelled') return 'border-rose-100 bg-rose-50 text-rose-700';
    return 'border-amber-100 bg-amber-50 text-amber-700';
  }
}
